import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Clock, Send, CheckCircle, AlertCircle, Loader, ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";

interface FormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  subject: string;
  message: string;
}

interface FormStatus {
  type: 'idle' | 'loading' | 'success' | 'error';
  message: string;
}

interface FormErrors {
  email?: string;
  phone?: string;
  message?: string;
}

export default function Contact() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });

  const [status, setStatus] = useState<FormStatus>({
    type: 'idle',
    message: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});

  // Validation functions
  const validateEmail = (email: string): string | undefined => {
    if (!email) return undefined;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return undefined;
  };

  const validatePhone = (phone: string): string | undefined => {
    if (!phone) return undefined;
    // Remove all non-digit characters for validation
    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length < 10) {
      return 'Phone number must be at least 10 digits';
    }
    if (cleanPhone.length > 15) {
      return 'Phone number must not exceed 15 digits';
    }
    return undefined;
  };

  const validateMessage = (message: string): string | undefined => {
    if (!message.trim()) {
      return 'Message is required';
    }
    if (message.length > 2000) {
      return 'Message must not exceed 2000 characters';
    }
    return undefined;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // For message field, limit to 2000 characters
    if (name === 'message' && value.length > 2000) {
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Real-time validation
    const newErrors = { ...errors };

    if (name === 'email') {
      const emailError = validateEmail(value);
      if (emailError) {
        newErrors.email = emailError;
      } else {
        delete newErrors.email;
      }
    }

    if (name === 'phone') {
      const phoneError = validatePhone(value);
      if (phoneError) {
        newErrors.phone = phoneError;
      } else {
        delete newErrors.phone;
      }
    }

    if (name === 'message') {
      const messageError = validateMessage(value);
      if (messageError) {
        newErrors.message = messageError;
      } else {
        delete newErrors.message;
      }
    }

    setErrors(newErrors);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all fields before submission
    const newErrors: FormErrors = {};

    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;

    const phoneError = validatePhone(formData.phone);
    if (phoneError) newErrors.phone = phoneError;

    const messageError = validateMessage(formData.message);
    if (messageError) newErrors.message = messageError;

    // Check if there are any validation errors
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setStatus({
        type: 'error',
        message: 'Please fix the errors below before submitting.'
      });
      return;
    }

    // Reset errors and status
    setErrors({});
    setStatus({ type: 'loading', message: 'Sending your message...' });

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setStatus({
          type: 'success',
          message: result.message || 'Thank you! Your message has been sent successfully.'
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          subject: '',
          message: ''
        });
      } else {
        setStatus({
          type: 'error',
          message: result.message || 'Failed to send message. Please try again.'
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setStatus({
        type: 'error',
        message: 'Network error. Please check your connection and try again.'
      });
    }
  };

  const contactInfo = [
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Email Us",
      details: ["contact@rlmineralsmining.com"],
      action: "mailto:contact@rlmineralsmining.com",
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: "Call Us",
      details: ["+91 98792 47225","+91 63751 95493"],
      action: "tel:+919879247225",
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Visit Us",
      details: [
        "R.L Minerals Mining",
        "S. NO. 222 - 226, opp. Vizag Steel, Azad Nagar",
        "Sarkhej, Ahmedabad",
        "Sarkhej-Okaf, Gujarat 382210",
      ],
      action:
        "https://maps.app.goo.gl/pAtusmz4y8Mu4rCP7",
    },
  ];



  const subjectOptions = [
    "General Inquiry",
    "Product Information",
    "Other"
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-primary/80 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <motion.h1
            className="text-4xl md:text-5xl font-bold mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Contact R.L Minerals Mining
          </motion.h1>
          <motion.p
            className="text-xl text-white/90 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Get in touch with our team for premium industrial minerals, bulk orders, and expert consultation
          </motion.p>
        </div>
      </section>


      <section className="py-16 bg-gradient-to-b from-white to-muted/40">
        <div className="container mx-auto px-4">
          {/* Heading */}
          <div className="text-center mb-12">
            <h2 className="text-4xl font-extrabold text-primary mb-3 inline-block relative">
              Get in Touch
              <span className="absolute left-1/2 -bottom-2 w-20 h-[3px] bg-accent rounded-full -translate-x-1/2"></span>
            </h2>
            <p className="text-foreground/70 text-base mt-3">
              We're here to answer your queries and provide the best assistance.
            </p>
          </div>

          {/* Contact Info Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center">
            {contactInfo.map((info, idx) => (
              <div
                key={idx}
                onClick={() => {info.action && window.open(info.action, "_blank");}}
                className="group relative bg-gradient-to-br from-primary/5 to-accent/5 rounded-xl p-6 shadow-md hover:shadow-lg hover:-translate-y-1 transition-all duration-300 w-full max-w-md"
              >
                {/* Glow background */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-accent/10 to-transparent opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500 rounded-xl" />

                {/* Icon */}
                <div className="relative flex items-center justify-center w-14 h-14 mb-4 rounded-full bg-accent/10 group-hover:bg-accent/20 transition-colors">
                  <span className="text-accent group-hover:scale-110 transition-transform duration-300">
                    {info.icon}
                  </span>
                </div>

                {/* Title */}
                <h3 className="relative font-semibold text-lg text-primary mb-2 group-hover:text-accent transition-colors">
                  {info.title}
                </h3>

                {/* Details */}
                <div className="relative space-y-1">
                  {info.details.map((detail, detailIdx) => (
                    <p
                      key={detailIdx}
                      className="text-foreground/70 text-sm group-hover:text-foreground transition-colors"
                    >
                      {detail}
                    </p>
                  ))}
                </div>

                {/* Accent underline on hover */}
                <span className="absolute left-0 bottom-0 w-0 h-[3px] bg-accent transition-all duration-300 group-hover:w-full rounded-full" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gradient-to-b from-white via-muted/20 to-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-14">
              <h2 className="relative inline-block text-3xl md:text-4xl font-extrabold text-primary mb-4">
                Send Us a Message
                {/* Animated underline */}
                <span className="block w-24 h-[4px] bg-accent rounded-full mx-auto mt-3 relative overflow-hidden">
                  <span className="absolute inset-0 bg-gradient-to-r from-accent to-primary animate-[shimmer_2s_infinite]" />
                </span>
              </h2>
              <p className="text-lg text-foreground/70 mt-3">
                Fill out the form below and we’ll get back to you within 24 hours.
              </p>
            </div>

            {/* Form Container */}
            <div className="bg-white/90 backdrop-blur-xl border border-gray-200 p-10 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Name and Email */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-primary mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your full name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-primary mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your email address"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all ${errors.email
                        ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300'
                        }`}
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle size={14} />
                        {errors.email}
                      </p>
                    )}
                  </div>
                </div>

                {/* Phone and Company */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-primary mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Enter your phone number"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all ${errors.phone
                        ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300'
                        }`}
                    />
                    {errors.phone && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle size={14} />
                        {errors.phone}
                      </p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-primary mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder="Enter your company name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all"
                    />
                  </div>
                </div>

                {/* Subject */}
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-primary mb-2">
                    Subject
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all"
                  >
                    <option value="">Select a subject</option>
                    {subjectOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-primary mb-2">
                    Message *
                    <span className="text-sm text-gray-500 font-normal">
                      ({formData.message.length}/2000 characters)
                    </span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    maxLength={2000}
                    placeholder="Tell us about your requirements, questions, or how we can help you..."
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all resize-vertical ${errors.message
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300'
                      }`}
                  />
                  {errors.message && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle size={14} />
                      {errors.message}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Maximum 2000 characters allowed
                  </p>
                </div>

                {/* Status Message */}
                {status.type !== "idle" && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-4 rounded-lg flex items-center gap-3 ${status.type === "success"
                      ? "bg-green-50 text-green-800 border border-green-200"
                      : status.type === "error"
                        ? "bg-red-50 text-red-800 border border-red-200"
                        : "bg-blue-50 text-blue-800 border border-blue-200"
                      }`}
                  >
                    {status.type === "loading" && <Loader className="w-5 h-5 animate-spin" />}
                    {status.type === "success" && <CheckCircle className="w-5 h-5" />}
                    {status.type === "error" && <AlertCircle className="w-5 h-5" />}
                    <span>{status.message}</span>
                  </motion.div>
                )}

                {/* Submit Button */}
                <div className="text-center">
                  <button
                    type="submit"
                    disabled={status.type === "loading"}
                    className="inline-flex items-center gap-3 bg-accent text-accent-foreground px-8 py-4 rounded-xl font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
                  >
                    {status.type === "loading" ? (
                      <>
                        <Loader className="w-5 h-5 animate-spin" />
                        Sending Message...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Send Message
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* shimmer animation keyframes */}
        <style>{`
    @keyframes shimmer {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
  `}</style>
      </section>

      {/* Additional Information */}
      <section className="py-20 bg-gradient-to-b from-muted/20 to-white">
        <div className="container mx-auto px-6">
          {/* Heading */}
          <div className="text-center mb-16">
            <h3 className="text-3xl md:text-4xl font-extrabold text-primary relative inline-block">
              Why Choose R.L Minerals Mining?
              <span className="absolute left-1/2 -bottom-3 w-24 h-[3px] bg-accent rounded-full -translate-x-1/2"></span>
            </h3>
            <p className="text-foreground/70 mt-6 max-w-2xl mx-auto text-base md:text-lg">
              Our commitment to excellence and innovation sets us apart in the mining and mineral export industry.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              {
                icon: "🏆",
                title: "Premium Quality",
                desc: "We deliver international-standard minerals backed by rigorous quality control.",
              },
              {
                icon: "🚚",
                title: "Reliable Delivery",
                desc: "On-time global delivery powered by a robust and efficient logistics network.",
              },
              {
                icon: "💼",
                title: "Expert Support",
                desc: "A dedicated technical team ensures smooth, custom-tailored solutions for your needs.",
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className="group relative bg-white p-8 rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-2 transition-all duration-500 text-center overflow-hidden"
              >
                {/* Glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-accent/10 to-transparent opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500"></div>

                {/* Icon */}
                <div className="relative text-5xl mb-6">{item.icon}</div>

                {/* Title */}
                <h4 className="relative font-semibold text-xl text-primary mb-3 group-hover:text-accent transition-colors">
                  {item.title}
                </h4>

                {/* Description */}
                <p className="relative text-foreground/70 text-sm md:text-base leading-relaxed group-hover:text-foreground transition-colors">
                  {item.desc}
                </p>

                {/* Underline animation */}
                <span className="absolute left-1/2 bottom-0 w-0 h-[3px] bg-accent rounded-full transition-all duration-300 group-hover:w-3/4 -translate-x-1/2"></span>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* Interactive Map Section */}
      <section className="py-20 bg-gradient-to-b from-white to-muted/20">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            {/* Heading */}
            <div className="text-center mb-16">
              <h3 className="text-3xl md:text-4xl font-extrabold text-primary relative inline-block">
                Our Location
                <span className="absolute left-1/2 -bottom-3 w-24 h-[3px] bg-accent rounded-full -translate-x-1/2"></span>
              </h3>
              <p className="text-foreground/70 mt-6 max-w-2xl mx-auto text-base md:text-lg">
                Visit our facilities or get directions to our manufacturing unit
              </p>
            </div>

            {/* Interactive Map */}
            <div className="mb-8">
              <div className="rounded-xl overflow-hidden shadow-lg h-[400px] md:h-[450px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3688.5857925828727!2d72.49752934233022!3d22.966156872458924!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0xf8676128b36919a6!2sR.L%20Minerals%20Mining!5e0!3m2!1sen!2sin!4v1700623456000"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="R.L Minerals Mining Location - Ahmedabad, Gujarat"
                />
              </div>

              {/* Map Controls */}
              <div className="mt-4 flex flex-wrap gap-3">
                <a
                  href="https://www.google.com/maps/place/R.L+Minerals+Mining/@22.9655074,72.4920512,14.25z/data=!4m10!1m2!2m1!1sR.L+Minerals+Mining,+S.+No.+222%E2%80%93226,+Nr.+Azad+Nagar,+Sarkhej%E2%80%93Dholka+Road,+New+Fatehvadi,+Ahmedabad,+Gujarat+382210!3m6!1s0x395e9b9d2cf5188d:0xf8676128b36919a6!8m2!3d22.965984!4d72.4975508!15sCnZSLkwgTWluZXJhbHMgTWluaW5nLCBTLiBOby4gMjIy4oCTMjI2LCBOci4gQXphZCBOYWdhciwgU2Fya2hlauKAk0Rob2xrYSBSb2FkLCBOZXcgRmF0ZWh3YWRpLCBBaG1lZGFiYWQsIEd1amFyYXQgMzgyMjEwkgERY2hlbWljYWxfZXhwb3J0ZXLgAQA!16s%2Fg%2F11yp7kgq7z?entry=ttu&g_ep=EgoyMDI1MTExNy4wIKXMDSoASAFQAw%3D%3D"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-lg hover:opacity-90 transition-opacity text-sm font-medium"
                >
                  <MapPin size={16} />
                  Open in Google Maps
                </a>

                <button
                  onClick={() => {
                    const DEST_LAT = 22.966156872458924;
                    const DEST_LNG = 72.49752934233022;
                    if (navigator.geolocation) {
                      navigator.geolocation.getCurrentPosition(
                         (position) => { //22.96616675087848, 72.49808724174396
                          const { latitude, longitude } = position.coords;
                          const destination = '&destination=https://maps.app.goo.gl/pAtusmz4y8Mu4rCP7';
                          const url = `https://www.google.com/maps/dir/?api=1&origin=${latitude},${longitude}&destination=${DEST_LAT},${DEST_LNG}&travelmode=driving`;
                          window.open(url, "_blank");
                        },
                        () => {
                          window.open(`https://www.google.com/maps/search/?api=1&query=${DEST_LAT},${DEST_LNG}`, "_blank");
                        }
                      );
                    } else {
                      window.open(`https://www.google.com/maps/search/?api=1&query=${DEST_LAT},${DEST_LNG}`, "_blank");
                    }
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity text-sm font-medium"
                >
                  <ArrowRight size={16} />
                  Get Directions
                </button>
              </div>
            </div>

            {/* Location Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Main Office */}
              <div className="group relative bg-gradient-to-br from-primary/5 to-accent/5 rounded-2xl p-8 shadow-md hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-accent/10 to-transparent opacity-0 group-hover:opacity-100 blur-2xl transition-opacity duration-500"></div>

                <div className="relative flex items-center justify-center w-14 h-14 mb-6 rounded-full bg-accent/10 group-hover:bg-accent/20 transition-colors">
                  <MapPin size={28} className="text-accent group-hover:scale-110 transition-transform duration-300" />
                </div>

                <h3 className="relative font-semibold text-lg text-primary mb-3 group-hover:text-accent transition-colors">
                  R.L Minerals Mining
                </h3>

                <div className="relative space-y-1.5 text-sm text-foreground/70 group-hover:text-foreground transition-colors">
                  <p>S. No. 222–226, Nr. Azad Nagar</p>
                  <p>Sarkhej–Dholka Road, New Fatehvadi</p>
                  <p>Ahmedabad – 382210, Gujarat, India</p>
                </div>

                <span className="absolute left-0 bottom-0 w-0 h-[3px] bg-accent transition-all duration-300 group-hover:w-full"></span>
              </div>

              {/* Manufacturing Unit */}
              <div className="group relative bg-gradient-to-br from-primary/5 to-accent/5 rounded-2xl p-8 shadow-md hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-accent/10 to-transparent opacity-0 group-hover:opacity-100 blur-2xl transition-opacity duration-500"></div>

                <div className="relative flex items-center justify-center w-14 h-14 mb-6 rounded-full bg-accent/10 group-hover:bg-accent/20 transition-colors">
                  <MapPin size={28} className="text-accent group-hover:scale-110 transition-transform duration-300" />
                </div>

                <h3 className="relative font-semibold text-lg text-primary mb-3 group-hover:text-accent transition-colors">
                  Manufacturing Unit
                </h3>

                {/* <div className="relative space-y-1.5 text-sm text-foreground/70 group-hover:text-foreground transition-colors">
                  <p>Chandesra Gudli Riico, Udaipur, Rajasthan</p>
                </div>
                <div className="relative space-y-1.5 text-sm text-foreground/70 group-hover:text-foreground transition-colors">
                  <p>Amet - Kelwa Road, Bamantukra, Rajsamand</p>
                </div> */}
                <div className="space-y-3">
                  {[
                    "Chandesra Gudli Riico, Udaipur, Rajasthan",
                    "Amet - Kelwa Road, Bamantukra, Rajsamand",
                  ].map((addr, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="h-6 w-6 flex items-center justify-center rounded-full bg-primary/10 text-primary">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M12 11.75a3.25 3.25 0 100-6.5 3.25 3.25 0 000 6.5z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M19.5 8.5c0 7-7.5 12-7.5 12S4.5 15.5 4.5 8.5a7.5 7.5 0 1115 0z"
                          />
                        </svg>

                      </div>
                      <p className="text-sm text-foreground/70">{addr}</p>
                    </div>
                  ))}
                </div>

                <span className="absolute left-0 bottom-0 w-0 h-[3px] bg-accent transition-all duration-300 group-hover:w-full"></span>
              </div>
            </div>
          </div>
        </div>
      </section>


    </Layout>
  );
}